import type { ContentIngestionProposedGroup } from "@contracts/contentIngestion";

/** Matches server `BulkImageIngestionController.NormalizeRelativePath` for analyze manifests. */
export function normalizeIngestionRelativePath(rawPath: string): string {
  return rawPath.replace(/\\/g, "/").split("/").filter(Boolean).join("/").trim();
}

/** Matches server `NormalizeFolderPrefix`. */
export function normalizeIngestionFolderPrefix(rawPrefix: string): string {
  return normalizeIngestionRelativePath(rawPrefix).replace(/\/+$/, "");
}

/** Matches server `PathMatchesFolderPrefix` (ordinal case-insensitive). */
export function pathMatchesIngestionFolderPrefix(relativePath: string, folderPrefix: string): boolean {
  if (!relativePath?.trim() || !folderPrefix?.trim()) {
    return false;
  }
  const p = relativePath;
  const prefix = folderPrefix;
  if (p.localeCompare(prefix, undefined, { sensitivity: "base" }) === 0) {
    return true;
  }
  return p.toLowerCase().startsWith(`${prefix.toLowerCase()}/`);
}

/**
 * Matches paths whose full relative path starts with `filenamePrefix` (case-insensitive).
 * e.g. `area/BigOleCentralPark` matches `area/BigOleCentralPark-14.jpg` and `area/BigOleCentralPark0abc.jpg`.
 */
export function pathMatchesIngestionFilenamePrefix(relativePath: string, filenamePrefix: string): boolean {
  if (!relativePath?.trim() || !filenamePrefix?.trim()) {
    return false;
  }
  const p = relativePath.toLowerCase();
  const prefix = filenamePrefix.toLowerCase();
  return p.startsWith(prefix) && p.length > prefix.length;
}

export const ATLAS_INGESTION_FALLBACK_GROUP_NAME = "Ungrouped photos";
export const ATLAS_INGESTION_FALLBACK_GROUP_RATIONALE = "Remaining files that were not covered by explicit file or folder-prefix mappings.";

/**
 * Best-effort backfill for groups that lack AI-provided folder prefixes (notably unmatched/excluded buckets).
 * Returns unique parent folders derived from file paths; empty when all files are root-level.
 */
export function backfillFolderPrefixesFromFilePaths(filePaths: Array<string>): Array<string> {
  const parentFolders = new Set<string>();
  for (const rawPath of filePaths) {
    const normalized = normalizeIngestionRelativePath(rawPath);
    const slashIndex = normalized.lastIndexOf("/");
    if (slashIndex > 0) {
      parentFolders.add(normalized.slice(0, slashIndex));
    }
  }
  return [...parentFolders].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

/**
 * The location a merged group keeps: the one location every merged group already has, otherwise none.
 * Merging a group that has a location with a group that has a different one, or none, would tag the second group's
 * photos with a business they were never matched to. So the location is dropped and the user picks it again, which
 * they can see on the card, rather than one group's location quietly applying to all of the photos.
 */
export function mergedGroupLocationId(locationIds: Array<string | null | undefined>): string | null {
  const first = locationIds[0]?.trim() || null;
  if (!first) {
    return null;
  }
  return locationIds.every(id => (id?.trim() || null) === first) ? first : null;
}

export interface ResolvedAtlasIngestionGroups {
  matchedGroups: Array<ContentIngestionProposedGroup>;
  unmatchedGroup: ContentIngestionProposedGroup | null;
}

interface IngestionPrefixSelector {
  groupIndex: number;
  prefix: string;
  matches: (relativePath: string) => boolean;
}

/**
 * Resolves which accepted files belong to each proposed group.
 *
 * The rules, in the order they are applied. Each one prevents a specific problem:
 *  1. An explicit file path wins over any prefix (the first group to list it wins). The AI uses these for outliers: a
 *     file that sits in one group's folder but belongs to another group.
 *  2. Otherwise the LONGEST matching prefix wins, whether it is a folder prefix or a filename stem.
 *       - The AI often gives a business its own group by carving its subfolder (or a filename stem) out of a broader
 *         folder that another group covers, ex. "restaurants/harbor-cafe" out of "restaurants". Resolved in list order,
 *         the broader group swallowed those files whenever it was listed first. The carved-out group then resolved to
 *         zero files and was dropped, so the cafe's photos uploaded under the wrong group, and with the wrong location.
 *       - It also keeps a short filename stem ("area/Big") from taking the files of a deeper folder group
 *         ("area/BigOleCentralPark"), which a plain "stems before folders" order would do.
 *  3. Prefixes of equal length go to the group listed first.
 *  4. Paths that match nothing are returned separately (not as a normal upload group), so the user decides about them.
 * Groups whose selectors don't overlap resolve the same in any order.
 */
export function resolveAtlasIngestionGroups(proposedGroups: Array<ContentIngestionProposedGroup>, acceptedRelativePaths: Array<string>): ResolvedAtlasIngestionGroups {
  const acceptedPaths = [...new Set(acceptedRelativePaths.map(p => normalizeIngestionRelativePath(p)).filter(Boolean))].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  const acceptedSet = new Set(acceptedPaths);

  const assignments = new Map<string, number>();

  // Rule 1: explicit file paths per group in order
  for (let gi = 0; gi < proposedGroups.length; gi++) {
    const explicit = (proposedGroups[gi].filePaths ?? []).map(p => normalizeIngestionRelativePath(p)).filter(Boolean);
    for (const p of explicit) {
      if (!acceptedSet.has(p)) {
        continue;
      }
      if (assignments.has(p)) {
        continue;
      }
      assignments.set(p, gi);
    }
  }

  // Rules 2 and 3: every group's folder prefixes and filename stems, most specific first.
  const selectors: Array<IngestionPrefixSelector> = [];
  for (let gi = 0; gi < proposedGroups.length; gi++) {
    for (const prefix of (proposedGroups[gi].folderPrefixes ?? []).map(normalizeIngestionFolderPrefix).filter(Boolean)) {
      selectors.push({ groupIndex: gi, prefix, matches: p => pathMatchesIngestionFolderPrefix(p, prefix) });
    }
    // Filename stems are the folder path plus a shared name portion, not a real folder.
    for (const stem of (proposedGroups[gi].filenamePrefixes ?? []).map(normalizeIngestionFolderPrefix).filter(Boolean)) {
      selectors.push({ groupIndex: gi, prefix: stem, matches: p => pathMatchesIngestionFilenamePrefix(p, stem) });
    }
  }
  // Array.sort is stable, so equal lengths stay in group order (rule 3).
  selectors.sort((a, b) => b.prefix.length - a.prefix.length);

  for (const p of acceptedPaths) {
    if (assignments.has(p)) {
      continue;
    }
    const mostSpecific = selectors.find(selector => selector.matches(p));
    if (mostSpecific) {
      assignments.set(p, mostSpecific.groupIndex);
    }
  }

  const result: Array<ContentIngestionProposedGroup> = proposedGroups.map((g, gi) => ({
    ...g,
    folderPrefixes: [...(g.folderPrefixes ?? [])],
    filenamePrefixes: [...(g.filenamePrefixes ?? [])],
    filePaths: acceptedPaths.filter(p => assignments.get(p) === gi).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
  }));

  const unassigned = acceptedPaths.filter(p => !assignments.has(p));
  const matchedGroups = result;
  const unmatchedGroup: ContentIngestionProposedGroup | null =
    unassigned.length === 0
      ? null
      : {
          groupId: crypto.randomUUID(),
          name: ATLAS_INGESTION_FALLBACK_GROUP_NAME,
          rationale: ATLAS_INGESTION_FALLBACK_GROUP_RATIONALE,
          filePaths: unassigned,
          folderPrefixes: [],
          filenamePrefixes: [],
        };

  return { matchedGroups, unmatchedGroup };
}
